import React, { useContext, useEffect, useMemo } from "react";
import {
  DrawerSelection,
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { MealWithIntakes, PlanCellWithIndex } from "../data/plan";
import {
  canBeConsumed,
  computePlanCellCoordinatesFromPlanCellWithIndex,
} from "../utils/planUtils";
import { formatPurchaseDescription } from "../utils/purchaseUtils";
import { DrawerHistoryContext, MealPlannerContext } from "../contexts";
import { usePurchase } from "../queries/caches";
import { EditMealDrawer } from "./EditMealDrawer";
import { IntakeDrawer } from "./intake/IntakeDrawer";
import { useImmer } from "use-immer";

function EditPlanCellHeader(props: {
  planCell: PlanCellWithIndex | undefined;
}) {
  const cell = props.planCell?.planCell;
  const purchase = usePurchase(
    cell?.indicator === "JustIntake" ? cell.purchaseUnitPurchaseId : undefined,
  );
  const heading = !cell
    ? ""
    : cell.indicator === "MealWithIntakes"
      ? cell.meal.dishDescription ||
        cell.meal.foodDescription ||
        cell.meal.purchaseDescription ||
        ""
      : formatPurchaseDescription(purchase);
  return <Plan2PlateDrawerHeader heading={heading} />;
}

export function EditPlanCellDrawer(props: {
  planCell: PlanCellWithIndex | undefined;
  onClose: () => void;
  open: boolean;
}) {
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const { isOpen, onOpen, onClose } =
    usePlan2PlateDisclosure(drawerHistoryContext);
  const {
    isOpen: isOpenIntake,
    onOpen: onOpenIntake,
    onClose: onCloseIntake,
  } = usePlan2PlateDisclosure(drawerHistoryContext, "intake");
  const [planCell, updatePlanCell] = useImmer(props.planCell);
  useEffect(() => {
    updatePlanCell(props.planCell);
  }, [props.planCell]);
  const { submitPlanCells } = useContext(MealPlannerContext);

  const intakeCoordinates = useMemo(
    () =>
      props.planCell
        ? computePlanCellCoordinatesFromPlanCellWithIndex(props.planCell)
        : undefined,
    [props.planCell],
  );

  const clickedCell = props.planCell?.planCell;
  const isMeal = clickedCell?.indicator === "MealWithIntakes";
  const isJustIntake = clickedCell?.indicator === "JustIntake";
  const hasIntakes = isMeal && !!(clickedCell as MealWithIntakes)?.intakes;

  return (
    <>
      {planCell && (
        <EditMealDrawer
          onClose={onClose}
          open={isOpen}
          onSubmit={props.onClose}
          planCells={[planCell]}
        />
      )}
      {intakeCoordinates && (
        <IntakeDrawer
          onClose={onCloseIntake}
          open={isOpenIntake}
          onSubmit={() => {
            onCloseIntake();
            props.onClose();
          }}
          planCellCoordinates={intakeCoordinates}
          focusPlanCell={props.planCell}
        />
      )}
      <Plan2PlateDrawer
        onClose={props.onClose}
        open={props.open}
        header={<EditPlanCellHeader planCell={props.planCell} />}
        body={
          <>
            {!props.planCell?.planCell.deleted &&
              props.planCell?.planCell?.indicator != "JustIntake" &&
              !hasIntakes && (
                <DrawerSelection
                  dataCy="drawer-selection-replan"
                  onClick={() => {
                    onOpen();
                  }}
                  icon="mealPlan"
                  heading={"Umplanen"}
                  description={"Passe den Ernährungsplan für diesen Eintrag an"}
                />
              )}
            {canBeConsumed(props.planCell) &&
              !props.planCell?.planCell.deleted &&
              props.planCell?.planCell?.indicator != "JustIntake" && (
                <DrawerSelection
                  dataCy={
                    hasIntakes
                      ? "drawer-selection-edit-consumption"
                      : "drawer-selection-consume"
                  }
                  onClick={() => onOpenIntake()}
                  icon="intake"
                  heading={hasIntakes ? "Verzehr bearbeiten" : "Verzehren"}
                  description={
                    hasIntakes
                      ? "Bearbeite Mengen und Einheiten des erfassten Verzehrs"
                      : "Erfasse genau, welche Mengen du tatsächlich gegessen hast"
                  }
                />
              )}
            {isJustIntake && !props.planCell?.planCell.deleted && (
              <DrawerSelection
                dataCy="drawer-selection-edit-consumption"
                onClick={() => onOpenIntake()}
                icon="intake"
                heading="Verzehr bearbeiten"
                description="Bearbeite Mengen und Einheiten des erfassten Verzehrs"
              />
            )}
            {props.planCell?.planCell && (
              <DrawerSelection
                dataCy="drawer-selection-delete-entry"
                onClick={() => {
                  planCell &&
                    submitPlanCells([
                      {
                        ...planCell,
                        planCell: {
                          ...planCell.planCell,
                          deleted: !planCell.planCell.deleted,
                        },
                      },
                    ]);
                  props.onClose();
                }}
                icon={planCell?.planCell.deleted ? "undo" : "delete"}
                heading={
                  planCell?.planCell.deleted ? "Wiederherstellen" : "Löschen"
                }
                description={
                  planCell?.planCell.deleted
                    ? "Stelle diesen Eintrag wieder her, wenn er versehentlich gelöscht wurde oder wenn du ihn erneut planen möchtest"
                    : "Entferne diesen Eintrag aus deinem Plan oder deiner Verzehrhistorie. Ungespeicherte Einträge werden unwiderruflich gelöscht"
                }
              />
            )}
          </>
        }
      />
    </>
  );
}
